/**
 * Related Records Service
 *
 * PE-769: Manages relationships between any objects in the CRM.
 * Allows linking records across different object types.
 */

import { createPersistedMockData } from './mockDataManager';
import { USE_GOBODO_API } from '../utils/gobodoUtils';
import { searchGobodoRecords } from './gobodo/gobodoRecordService';
import { getRecordDisplayName as getGobodoRecordDisplayName } from '../utils/recordDisplayName';

/**
 * Related Record Link
 * Represents a relationship between two records
 */
export interface RelatedRecord {
  id: string;
  /** Source record ID */
  recordId: string;
  /** Source object type (e.g., 'Task__c', 'Client__c') */
  objectType: string;
  /** Related record ID */
  relatedRecordId: string;
  /** Related object type */
  relatedObjectType: string;
  /** Relationship type */
  relationshipType: 'related_to' | 'child_of' | 'parent_of';
  /** User who created the link */
  createdBy: string;
  createdByName: string;
  /** When the link was created */
  createdAt: string;
}

/**
 * Object type configuration for lookups
 */
export interface ObjectTypeConfig {
  apiName: string;
  label: string;
  pluralLabel: string;
  icon: string;
  nameField: string; // Field used for display name
  storageKey: string; // localStorage key for this object's records
}

/**
 * Available object types for relating
 */
export const RELATABLE_OBJECTS: ObjectTypeConfig[] = [
  { apiName: 'Client__c', label: 'Client', pluralLabel: 'Clients', icon: 'Users', nameField: 'name', storageKey: 'crm_client_records' },
  { apiName: 'Contact__c', label: 'Contact', pluralLabel: 'Contacts', icon: 'User', nameField: 'name', storageKey: 'crm_contact_records' },
  { apiName: 'Lead__c', label: 'Lead', pluralLabel: 'Leads', icon: 'UserPlus', nameField: 'name', storageKey: 'crm_lead_records' },
  { apiName: 'Opportunity__c', label: 'Opportunity', pluralLabel: 'Opportunities', icon: 'DollarSign', nameField: 'name', storageKey: 'crm_opportunity_records' },
  { apiName: 'Case__c', label: 'Case', pluralLabel: 'Cases', icon: 'Briefcase', nameField: 'subject', storageKey: 'crm_case_records' },
  { apiName: 'Task__c', label: 'Task', pluralLabel: 'Tasks', icon: 'CheckSquare', nameField: 'title', storageKey: 'crm_task_records' },
  { apiName: 'Event__c', label: 'Event', pluralLabel: 'Events', icon: 'Calendar', nameField: 'title', storageKey: 'crm_event_records' },
];

/**
 * Default related records (empty)
 */
const defaultRelatedRecords: RelatedRecord[] = [];

/**
 * Persisted Related Records Storage
 */
export const persistedRelatedRecords = createPersistedMockData<RelatedRecord>(
  'crm_related_records',
  defaultRelatedRecords
);

/**
 * Get all related records for a specific record
 */
export function getRelatedRecords(recordId: string, objectType: string): RelatedRecord[] {
  const allRecords = persistedRelatedRecords.get();

  // Get records where this record is either the source or the related
  return allRecords.filter(r =>
    (r.recordId === recordId && r.objectType === objectType) ||
    (r.relatedRecordId === recordId && r.relatedObjectType === objectType)
  );
}

/**
 * Add a new related record link
 */
export function addRelatedRecord(
  recordId: string,
  objectType: string,
  relatedRecordId: string,
  relatedObjectType: string,
  relationshipType: 'related_to' | 'child_of' | 'parent_of' = 'related_to',
  createdBy: string = 'user_1',
  createdByName: string = 'John Smith'
): RelatedRecord {
  const newRecord: RelatedRecord = {
    id: `rel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    relatedRecordId,
    relatedObjectType,
    relationshipType,
    createdBy,
    createdByName,
    createdAt: new Date().toISOString()
  };

  persistedRelatedRecords.add(newRecord);
  return newRecord;
}

/**
 * Remove a related record link
 */
export function removeRelatedRecord(relationId: string): boolean {
  return persistedRelatedRecords.remove(relationId);
}

/**
 * Check if a relationship already exists
 */
export function relationshipExists(
  recordId: string,
  objectType: string,
  relatedRecordId: string,
  relatedObjectType: string
): boolean {
  const allRecords = persistedRelatedRecords.get();

  return allRecords.some(r =>
    // Check both directions
    (r.recordId === recordId && r.objectType === objectType &&
     r.relatedRecordId === relatedRecordId && r.relatedObjectType === relatedObjectType) ||
    (r.recordId === relatedRecordId && r.objectType === relatedObjectType &&
     r.relatedRecordId === recordId && r.relatedObjectType === objectType)
  );
}

/**
 * Get records from a specific object type for lookup
 * Uses Gobodo Search API when USE_GOBODO_API is true, otherwise uses localStorage
 */
export async function getRecordsForLookup(objectType: string, searchTerm?: string): Promise<any[]> {
  const config = RELATABLE_OBJECTS.find(o => o.apiName === objectType);
  if (!config) return [];

  if (USE_GOBODO_API) {
    try {
      // Use Gobodo Search API
      const query = searchTerm || '';
      const records = await searchGobodoRecords(query, objectType, { max: 50 });
      
      // Map records to expected format with display names
      return records.map(record => ({
        ...record,
        displayName: getGobodoRecordDisplayName(objectType, record)
      }));
    } catch (error) {
      console.error(`[getRecordsForLookup] Failed to search records for ${objectType}:`, error);
      return [];
    }
  } else {
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem(config.storageKey);
      if (!stored) return [];

      const records = JSON.parse(stored) as any[];

      if (!searchTerm) return records.slice(0, 50); // Limit for performance

      const search = searchTerm.toLowerCase();
      return records.filter(r => {
        const name = r[config.nameField] || r.name || r.title || r.subject || '';
        return name.toLowerCase().includes(search);
      }).slice(0, 20);
    } catch (error) {
      console.error(`Failed to get records for ${objectType}:`, error);
      return [];
    }
  }
}

/**
 * Get a single record by ID from any object type
 */
export function getRecordById(objectType: string, recordId: string): any | null {
  const config = RELATABLE_OBJECTS.find(o => o.apiName === objectType);
  if (!config) return null;

  try {
    const stored = localStorage.getItem(config.storageKey);
    if (!stored) return null;

    const records = JSON.parse(stored) as any[];
    return records.find(r => r.id === recordId) || null;
  } catch (error) {
    console.error(`Failed to get record ${recordId} from ${objectType}:`, error);
    return null;
  }
}

/**
 * Get the display name for a record
 */
export function getRecordDisplayName(objectType: string, record: any): string {
  const config = RELATABLE_OBJECTS.find(o => o.apiName === objectType);
  if (!config) return record.id;

  return record[config.nameField] || record.name || record.title || record.subject || record.id;
}

/**
 * Get object type configuration
 */
export function getObjectTypeConfig(objectType: string): ObjectTypeConfig | undefined {
  return RELATABLE_OBJECTS.find(o => o.apiName === objectType);
}
